import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { PaintBrush, UploadSimple } from "@phosphor-icons/react";

export default function DesignerDashboard() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadFor, setUploadFor] = useState(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/briefs").then(r => setBriefs(r.data)).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!file) { toast.error("Pick an image"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        await api.post(`/briefs/${uploadFor}/upload-design`, { image_base64: base64, note });
        toast.success("Design delivered! Client has been notified.");
        setUploadFor(null); setFile(null); setNote("");
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
      } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const active = briefs.filter(b => b.status === "designing");
  const done = briefs.filter(b => ["review","approved","ordered"].includes(b.status));

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Designer · Workspace</div>
        <h1 className="text-5xl font-black tracking-tighter mb-10 flex items-center gap-4">
          <PaintBrush size={48} weight="duotone" /> Your briefs.
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[var(--pf-border)] mb-10">
          {[["Assigned", active.length], ["Completed", done.length], ["Total", briefs.length], ["This month", briefs.filter(b => new Date(b.created_at).getMonth() === new Date().getMonth()).length]].map(([l,v]) => (
            <div key={l} className="border-r border-b border-[var(--pf-border)] p-6" data-testid={`d-stat-${l.toLowerCase()}`}>
              <div className="pf-label">{l}</div>
              <div className="font-mono text-3xl font-black mt-2">{v}</div>
            </div>
          ))}
        </div>

        <section className="mb-12">
          <div className="pf-label mb-2">Section · Active queue</div>
          <h2 className="text-3xl font-black tracking-tight mb-6">Briefs to deliver</h2>
          {loading ? <div className="pf-label">Loading…</div> : active.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]" data-testid="no-briefs">
              No briefs assigned yet. Admin will send new work here.
            </div>
          ) : (
            <div className="border-2 border-[var(--pf-brand)]">
              {active.map(b => (
                <div key={b.id} className="grid grid-cols-12 gap-4 items-start p-5 border-b last:border-0 border-[var(--pf-border)]" data-testid={`brief-${b.id}`}>
                  <div className="col-span-4">
                    <div className="font-mono text-xs text-[var(--pf-text-2)]">#{b.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold text-lg mt-1">{b.title}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-1">{b.product_type.replace('_',' ')} · {b.industry} · {b.quantity} pcs</div>
                  </div>
                  <div className="col-span-6 text-sm text-[var(--pf-text-2)]">{b.description}</div>
                  <div className="col-span-2 text-right">
                    <button onClick={()=>setUploadFor(b.id)} className="pf-btn-primary text-sm" data-testid={`upload-${b.id}`}>
                      <UploadSimple size={14}/> Upload
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {done.length > 0 && (
          <section>
            <div className="pf-label mb-2">Section · Archive</div>
            <h2 className="text-3xl font-black tracking-tight mb-6">Completed</h2>
            <div className="border border-[var(--pf-border)]">
              {done.map(b => (
                <Link key={b.id} to={`/brief/${b.id}`} className="grid grid-cols-12 gap-4 items-center p-4 border-b last:border-0 border-[var(--pf-border)] hover:bg-white">
                  <div className="col-span-5 font-semibold">{b.title}</div>
                  <div className="col-span-4 text-sm text-[var(--pf-text-2)]">{b.product_type.replace('_',' ')} · {b.quantity}</div>
                  <div className="col-span-3 text-right">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-surface-2)]">{b.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {uploadFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={()=>setUploadFor(null)}>
          <div className="bg-white border-2 border-[var(--pf-text)] max-w-md w-full p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="pf-label mb-2">Upload final design</div>
            <h3 className="text-2xl font-black tracking-tight mb-5">Deliver to client</h3>
            <input type="file" accept="image/*" onChange={(e)=>setFile(e.target.files?.[0])} className="mb-4" data-testid="upload-file" />
            <textarea rows={3} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Optional note to client (revisions, print specs…)"
              className="w-full border border-[var(--pf-border)] p-3 mb-4 text-sm" data-testid="upload-note" />
            <div className="flex justify-end gap-2">
              <button onClick={()=>setUploadFor(null)} className="pf-btn-outline text-sm">Cancel</button>
              <button onClick={submit} disabled={uploading || !file} className="pf-btn-primary text-sm" data-testid="upload-submit">
                {uploading ? "Uploading…" : "Deliver →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
